/**
 * ─── Arena Entity Definitions ────────────────────────────────
 * Self-registers all Arena entities with the Entity Registry.
 *
 * Entities:
 *  - arenas              — Fighting pit venues
 *  - arena_fighters      — Participating entries
 *  - arena_matches       — Scheduled fights
 *  - arena_members       — Membership accounts
 *  - arena_bets          — Wagers on matches
 *  - arena_entrances     — Entrance tickets
 */

import { EntityRegistry } from '@repo/core'
import type { EntityDefinition } from '@repo/core'
import type {
  Arena, Fighter, Match, ArenaMember, Bet, Entrance,
} from './arena.schema'
import { ArenaHooks } from './arena.hooks'

// ─── Arena ─────────────────────────────────────────────────

export const ArenaEntity: EntityDefinition<Arena> = {
  name: 'arenas',
  ui: {
    label: 'Arena',
    labelPlural: 'Arenas',
    icon: 'Building2',
    routePath: 'arena/arenas',
    color: 'red',
    showInNav: true,
    navOrder: 10,
    navGroup: 'Arena',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'arena' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Fighter ───────────────────────────────────────────────

export const FighterEntity: EntityDefinition<Fighter> = {
  name: 'arena_fighters',
  ui: {
    label: 'Fighter',
    labelPlural: 'Fighters',
    icon: 'Swords',
    routePath: 'arena/fighters',
    color: 'yellow',
    showInNav: true,
    navOrder: 20,
    navGroup: 'Arena',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'arena_fighter' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Match ─────────────────────────────────────────────────

export const MatchEntity: EntityDefinition<Match> = {
  name: 'arena_matches',
  ui: {
    label: 'Match',
    labelPlural: 'Matches',
    icon: 'Trophy',
    routePath: 'arena/matches',
    color: 'purple',
    showInNav: true,
    navOrder: 30,
    navGroup: 'Arena',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'arena_match' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Arena Member ──────────────────────────────────────────

export const ArenaMemberEntity: EntityDefinition<ArenaMember> = {
  name: 'arena_members',
  ui: {
    label: 'Member',
    labelPlural: 'Members',
    icon: 'Users',
    routePath: 'arena/members',
    color: 'blue',
    showInNav: true,
    navOrder: 5,
    navGroup: 'Arena',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'arena_member' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Bet ───────────────────────────────────────────────────

export const BetEntity: EntityDefinition<Bet> = {
  name: 'arena_bets',
  ui: {
    label: 'Bet',
    labelPlural: 'Bets',
    icon: 'Coins',
    routePath: 'arena/bets',
    color: 'green',
    showInNav: true,
    navOrder: 40,
    navGroup: 'Arena',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'arena_bet' },
  hooks: {},
  pagination: 'cursor',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

// ─── Entrance ──────────────────────────────────────────────

export const EntranceEntity: EntityDefinition<Entrance> = {
  name: 'arena_entrances',
  ui: {
    label: 'Entrance',
    labelPlural: 'Entrances',
    icon: 'Ticket',
    routePath: 'arena/entrances',
    color: 'blue',
    showInNav: true,
    navOrder: 50,
    navGroup: 'Arena',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'arena_entrance' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Self-Register All ─────────────────────────────────────

EntityRegistry.register(ArenaEntity)
EntityRegistry.register(FighterEntity)
EntityRegistry.register(MatchEntity)
EntityRegistry.register(ArenaMemberEntity)
EntityRegistry.register(BetEntity)
EntityRegistry.register(EntranceEntity)
